import { FC, useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { LoginTab } from './LoginTab'
import { CreateAccountTab } from './CreateAccountTab'
import useAuthStore from '@/auth/authStore'
import ForgotPassword from './forgotPassword'
import { Logo } from '@/common/assets/svg'
import { H3 } from '@/common/ui'
import SolidBackgroundModal from '@/common/ui/SolidBackgroundModal'
import { usePreloadCampaignsAndImages } from '@/campaigns/usePreloadCampaignsAndImages'
import { ONBOARDING_COPY } from '@/locales/en'
import { Navigate } from 'react-router-dom'
import { APP_ROUTES } from '@/common/constants'
import { AuthTab } from './types'

type TabProps = {
    label: string
    value: string
}

const Tab: FC<TabProps> = ({ label, value }) => {
    return (
        <TabsTrigger
            value={value}
            className="data-[state=active]:font-semibold text-foreground w-full py-3 rounded-b-none data-[state=active]:bg-secondary-light"
        >
            {label}
        </TabsTrigger>
    )
}

export const AuthScreen = ({ tab }: { tab?: AuthTab }) => {
    const defaultTab = tab ?? AuthTab.LOGIN

    const [activeTab, setActiveTab] = useState<AuthTab>(defaultTab)
    // keep local state in sync when `tab` prop changes (route navigation)
    useEffect(() => {
        setActiveTab(tab || AuthTab.LOGIN)
    }, [tab])
    const { isForgotPassword, setIsForgotPassword } = useAuthStore()
    const authDetails = useAuthStore((s) => s.authDetails)

    const handleTabChange = (value: string) => {
        setActiveTab(value as AuthTab)
    }

    const hideResetPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        setIsForgotPassword(false)
    }

    // Preload campaigns while user is on the auth screen
    usePreloadCampaignsAndImages()

    if (authDetails) return <Navigate to={APP_ROUTES.DASHBOARD} replace />

    return (
        <SolidBackgroundModal>
            <div className="flex flex-col gap-6">
                <Logo className="h-[34px] md:h-[40px] mx-auto" />
                {isForgotPassword ? (
                    <ForgotPassword onSignInClick={hideResetPassword} />
                ) : (
                    <>
                        {activeTab === 'login' && (
                            <H3 className="text-center text-3xl">{ONBOARDING_COPY.login.loginTitle}</H3>
                        )}
                        {activeTab === 'signup' && (
                            <>
                                <H3 className="text-center mb-0 text-3x leading-9">
                                    {ONBOARDING_COPY.login.signupTitle}
                                </H3>
                                <p className="text-sm text-center">{ONBOARDING_COPY.login.signupDescription}</p>
                            </>
                        )}

                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                            <TabsList className="p-0 h-auto bg-background flex justify-center">
                                <Tab label="Log in" value="login" />
                                <Tab label="Create an account" value="signup" />
                            </TabsList>
                            <div className="h-[2px] bg-primary-light" />
                            <TabsContent tabIndex={-1} className="pt-4" value="login">
                                <LoginTab />
                            </TabsContent>
                            <TabsContent tabIndex={-1} className="pt-4" value="signup">
                                <CreateAccountTab />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </div>
        </SolidBackgroundModal>
    )
}
